import type { ServerStatus } from '@comficonf/shared';
import { infiniteQueryOptions, queryOptions, useQuery } from '@tanstack/react-query';
import { api } from './instance';
import { queryKeys } from './keys';

export const sessionQuery = () =>
  queryOptions({
    queryKey: queryKeys.session,
    queryFn: ({ signal }) => api.getSession(signal),
    staleTime: 5 * 60_000,
    retry: false,
  });

export const serversQuery = () =>
  queryOptions({
    queryKey: queryKeys.servers,
    queryFn: ({ signal }) => api.listServers(signal).then((r) => r.servers),
  });

export const serverQuery = (serverId: string) =>
  queryOptions({
    queryKey: queryKeys.server(serverId),
    queryFn: ({ signal }) => api.getServer(serverId, signal).then((r) => r.server),
  });

export const pluginsQuery = (serverId: string) =>
  queryOptions({
    queryKey: queryKeys.plugins(serverId),
    queryFn: ({ signal }) => api.listPlugins(serverId, signal).then((r) => r.plugins),
  });

/** Configuration values leave the query cache as soon as no editor shows them. */
export const resourceQuery = (serverId: string, resourceId: string) =>
  queryOptions({
    queryKey: queryKeys.resource(serverId, resourceId),
    queryFn: ({ signal }) => api.getResource(serverId, resourceId, signal),
    staleTime: 30_000,
    gcTime: 0,
    refetchOnWindowFocus: false,
  });

/** The material catalog is cached for the whole session. */
export const materialsQuery = (serverId: string) =>
  queryOptions({
    queryKey: queryKeys.materials(serverId),
    queryFn: ({ signal }) => api.getMaterials(serverId, signal),
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
  });

export const AUDIT_PAGE_SIZE = 20;

export const auditQuery = (serverId: string) =>
  infiniteQueryOptions({
    queryKey: queryKeys.audit(serverId),
    queryFn: ({ signal, pageParam }) =>
      api.getAudit(serverId, { limit: AUDIT_PAGE_SIZE, before: pageParam }, signal),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

export function useSession() {
  return useQuery(sessionQuery());
}

/** Live status of a server: the detail query when loaded, else the list entry. */
export function useServerStatus(serverId: string): ServerStatus | undefined {
  const detail = useQuery({ ...serverQuery(serverId), enabled: false });
  const list = useQuery({ ...serversQuery(), enabled: false });
  return detail.data?.status ?? list.data?.find((s) => s.id === serverId)?.status;
}
